/**
 * Слова текста книги с позициями в исходной строке — общий токенизатор для словника (сервер)
 * и слоя ударений просмотрщика (клиент, часть 3): одинаковые правила дают одинаковое «k-е появление слова».
 * Перенос строки PDFium (U+FFFE) и мягкий перенос (U+00AD) внутри слова пропускаются; U+0301 остаётся частью слова.
 * Перенос дефисом в конце строки склеивается: «рубаш-↵ка» — «рубашка», «Кабеса-де-↵Вака» (заглавная) — с дефисом.
 * Дефис — «-» и типографские U+2010, U+2011 (неразрывным набраны переносы в «Книге записей»); в слове он всегда «-».
 */
import { ACUTE, hasLatin, normalizeKey } from './notation.js';

export type Token = { text: string; key: string; start: number; end: number; latin: boolean; stressedInText: boolean };

const LETTER = /\p{L}/u;
/** ª и º — «буквы» для Unicode, а в PDF с испорченной кодировкой это мусор вместо кавычек и тире («Мосфильмаª») */
const NOT_LETTERS = new Set(['ª', 'º']);
const isLetter = (ch: string) => LETTER.test(ch) && !NOT_LETTERS.has(ch);
const JOINERS = new Set(['￾', '­']);
/** Символы дефиса для регулярных выражений: «-», U+2010, U+2011 */
export const HYPHEN_CHARS = '\\-\u2010\u2011';
const HYPHEN = new RegExp(`^[${HYPHEN_CHARS}]$`, 'u');
export const isHyphen = (ch: string) => HYPHEN.test(ch);
const LINE_SPACE = /[ \t\r\n]/;

/**
 * Перенос дефисом: после дефиса только пробелы, переводы строк и метки переноса PDF (U+FFFE, U+00AD), хотя бы один
 * перевод или метка, дальше буква. resume — индекс этой буквы; marked — была метка: PDF сам говорит, что «-» — настоящий
 * дефис на переносе («какого-\u00ADнибудь»), его оставляем.
 */
function lineBreakHyphenEnd(chars: readonly string[], hyphen: number): { resume: number; marked: boolean } | null {
  let j = hyphen + 1;
  let sawBreak = false;
  let marked = false;
  while (j < chars.length && (LINE_SPACE.test(chars[j]) || JOINERS.has(chars[j]))) {
    if (chars[j] === '\n') sawBreak = true;
    if (JOINERS.has(chars[j])) marked = true;
    j++;
  }
  return (sawBreak || marked) && j < chars.length && isLetter(chars[j]) ? { resume: j, marked } : null;
}

export function tokenizeWords(text: string): Token[] {
  const out: Token[] = [];
  const chars = [...text];
  // Индексы — в единицах UTF-16, как у PDFium (extractText): копим длину по ходу
  let offset = 0;
  let i = 0;
  while (i < chars.length) {
    if (!isLetter(chars[i])) {
      offset += chars[i].length;
      i++;
      continue;
    }
    const start = offset;
    let word = '';
    let stressedInText = false;
    while (i < chars.length) {
      const ch = chars[i];
      const next = chars[i + 1] ?? '';
      if (isLetter(ch)) word += ch;
      else if (ch === ACUTE) {
        word += ch;
        stressedInText = true;
      } else if (JOINERS.has(ch) && isLetter(next)) {
        // пропуск: слово продолжается на следующей строке
      } else if (isHyphen(ch) && isLetter(next) && word.length > 0) word += '-';
      else if (isHyphen(ch) && word.length > 0) {
        const wrap = lineBreakHyphenEnd(chars, i);
        if (wrap === null) break;
        if (wrap.marked || chars[wrap.resume] !== chars[wrap.resume].toLowerCase()) word += '-';
        for (let k = i; k < wrap.resume; k++) offset += chars[k].length;
        i = wrap.resume;
        continue;
      } else break;
      offset += ch.length;
      i++;
    }
    out.push({ text: word, key: normalizeKey(word), start, end: offset, latin: hasLatin(word), stressedInText });
  }
  return out;
}
